import { Controller } from "@hotwired/stimulus"
import { LocationMixin } from "./product-form/location"
import { getI18nMessage } from "../lib/i18n_helper"
import { NativeBridge } from "../lib/native_bridge"
import { createLogger } from "../lib/logger"

const log = createLogger('RescueAnimalForm')

// Rescue Animal Form Controller — interactive self-hosted map-picker + GPS (listing-wizard pattern)
export default class extends Controller {
  static targets = [
    "addressField",
    "locationStatus",
    "latitudeDisplay",
    "longitudeDisplay",
    "latitudeField",
    "longitudeField",
    "currentLocationButton",
    "gpsIcon",
    "addressError"
  ]

  static values = {
    currentStep: Number
  }

  connect() {
    this.reverseGeocode = LocationMixin.reverseGeocode.bind(this)
    this.syncLocationDisplay()
    log.info('Connected', { step: this.currentStepValue })
  }

  // --- Location Methods ---

  async useCurrentLocation(event) {
    const button = event.currentTarget
    const { latitude, longitude, address } = button.dataset

    this.setLocationLoading(true)

    try {
      if (NativeBridge.isNative()) {
        try {
          const { latitude, longitude } = await NativeBridge.GPS.requestLocation({ accuracy: "high" })
          await this.applyLocation(latitude, longitude)
          return
        } catch (nativeError) {
          log.warn('Native GPS failed, falling back to browser', nativeError)
        }
      }

      if (navigator.geolocation) {
        this.updateLocationStatus(getI18nMessage(this.element, 'locationFetchingCurrent'))
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            await this.applyLocation(position.coords.latitude, position.coords.longitude)
          },
          (error) => {
            log.warn('Browser geolocation failed', error)
            if (latitude && longitude) {
              this.applyLocationFromData(parseFloat(latitude), parseFloat(longitude), address)
            } else {
              this.updateLocationStatus(getI18nMessage(this.element, 'locationFetchFailedFallback'))
            }
            this.setLocationLoading(false)
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        )
        return
      }

      if (latitude && longitude) {
        this.applyLocationFromData(parseFloat(latitude), parseFloat(longitude), address)
      } else {
        this.updateLocationStatus(getI18nMessage(this.element, 'locationNotSupportedFallback'))
      }
    } finally {
      this.setLocationLoading(false)
    }
  }

  async applyLocation(lat, lng) {
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = lat
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = lng

    this.updateCoordinateDisplay(lat, lng)

    try {
      this.updateLocationStatus(getI18nMessage(this.element, 'locationGeocodingLoading'))

      const address = await this.reverseGeocode(lat, lng)
      if (address && this.hasAddressFieldTarget) {
        this.addressFieldTarget.value = address
        this.updateLocationStatus(getI18nMessage(this.element, 'locationApplied'))
      }
    } catch (err) {
      log.warn('Reverse geocode failed', err)
      this.updateLocationStatus(getI18nMessage(this.element, 'locationGeocodingFailed'))
    }

    this.updateMapPicker(lat, lng)
    this.setLocationLoading(false)
  }

  applyLocationFromData(lat, lng, address) {
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = lat
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = lng
    if (address && this.hasAddressFieldTarget) this.addressFieldTarget.value = address
    this.updateCoordinateDisplay(lat, lng)
    this.updateLocationStatus(getI18nMessage(this.element, 'locationApplied'))
    this.updateMapPicker(lat, lng, address)
  }

  handleAddressSelected(event) {
    const { latitude, longitude, address } = event.detail
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = latitude
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = longitude
    if (this.hasAddressFieldTarget) this.addressFieldTarget.value = address
    this.updateCoordinateDisplay(latitude, longitude)
    this.updateMapPicker(latitude, longitude, address)
    this.updateLocationStatus(getI18nMessage(this.element, 'locationAutocompleteSelected'))
  }

  // --- Interactive map-picker (self-hosted MapLibre) ---

  // Move the sibling map-picker pin + recenter when GPS/address sets the location. Mirrors
  // listing_wizard#updateMapPicker: locate the map-picker controller and call its public updateMarker()
  // (moves pin, pans map, writes the shared hidden lat/lng fields). No-ops when no map-picker is mounted.
  updateMapPicker(latitude, longitude, address = null) {
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return

    const el = this.element.querySelector("[data-controller~='map-picker']")
    if (!el) return

    const picker = this.application.getControllerForElementAndIdentifier(el, "map-picker")
    picker?.updateMarker?.(lat, lng, address)
  }

  // The user dragged/tapped the pin — the shared hidden lat/lng fields are already written by the
  // map-picker. Keep the visible coordinate display + address honest.
  handleMapCoordinateChange(event) {
    const { latitude, longitude, address } = event.detail
    if (latitude != null && longitude != null) this.updateCoordinateDisplay(latitude, longitude)
    if (address && this.hasAddressFieldTarget) this.addressFieldTarget.value = address
  }

  // Map-picker finished reverse-geocoding the dragged pin → sync the visible address.
  handleAddressResolved(event) {
    const { address } = event.detail
    if (address && this.hasAddressFieldTarget) this.addressFieldTarget.value = address
  }

  // --- UI Helpers ---

  syncLocationDisplay() {
    const lat = this.hasLatitudeFieldTarget ? this.latitudeFieldTarget.value : null
    const lng = this.hasLongitudeFieldTarget ? this.longitudeFieldTarget.value : null

    if (lat && lng && parseFloat(lat) !== 0) {
      this.updateCoordinateDisplay(parseFloat(lat), parseFloat(lng))
    }
  }

  updateCoordinateDisplay(lat, lng) {
    if (this.hasLatitudeDisplayTarget) {
      this.latitudeDisplayTarget.textContent = parseFloat(lat).toFixed(6)
    }
    if (this.hasLongitudeDisplayTarget) {
      this.longitudeDisplayTarget.textContent = parseFloat(lng).toFixed(6)
    }
  }

  updateLocationStatus(message) {
    if (this.hasLocationStatusTarget) {
      this.locationStatusTarget.textContent = message
    }
  }

  setLocationLoading(isLoading) {
    if (this.hasCurrentLocationButtonTarget) {
      this.currentLocationButtonTarget.disabled = isLoading
      if (isLoading) {
        this.currentLocationButtonTarget.classList.add('opacity-60')
      } else {
        this.currentLocationButtonTarget.classList.remove('opacity-60')
      }
    }
    if (this.hasGpsIconTarget) {
      if (isLoading) {
        this.gpsIconTarget.classList.add('animate-spin')
      } else {
        this.gpsIconTarget.classList.remove('animate-spin')
      }
    }
  }
}
